/**
 * ONE TAB STOP — INCLUDING WHILE A ROW IS BEING EDITED (WCAG 2.4.3).
 *
 *   view mode        ┌───────────────────────────────┐  the roving tabindex="0"
 *                    │ [Name]    Note     Amount     │  sits on the focused
 *                    └───────────────────────────────┘  header / cell        ✓
 *
 *   editing, as      ┌───────────────────────────────┐  every cell drops to -1
 *   MUI ships it     │  Name     Note     Amount     │  GridCell.js:122
 *                    └───────────────────────────────┘
 *                              ▼ portaled to <body>
 *                        ┌──────────┐                   the ONLY reachable
 *                        │[ Alpha  ]│                   thing is outside the
 *                        └──────────┘                   grid entirely         ✗
 *
 * MUI drops an editable cell to `tabindex="-1"` on the premise that the editor
 * it holds is INSIDE it, so the input is the tab stop and the cell must not
 * double as one. `PopoverEditCell` re-parents the live editor to `<body>`, so
 * that premise is false here: the grid is left with ZERO tab stops, and a
 * keyboard user who lands outside it mid-edit can never Tab back in.
 *
 * The repair is the exact inverse of that one clause, and nothing more: hand
 * the tab stop back to the ONE cell MUI is ALREADY tracking it on.
 *
 *   state.tabIndex.cell   measured intact all the way through row edit mode
 *          │              (`{ id: 0, field: 'name' }` while all three cells
 *          │               render -1) — MUI never clears it, it only declines
 *          ▼               to RENDER it
 *   exactly one cell can match  ─▶  at most one CELL carries the tab stop
 *
 * The row's OTHER editable columns still render their editors inline, and a
 * bare `<input>` is tabbable with no tabindex at all; `PopoverEditCell` takes
 * those out of the tab order so this hook's one cell is the only one left.
 *
 * NOT A NEW KEYMAP. This changes where the browser's Tab can LAND from
 * outside; it adds no leave-cell path, so the One Law (`keymap.contract`)
 * is untouched — the document keymap still claims Tab in the capture phase
 * whenever an editor holds focus.
 */

import { type GridRowId, gridTabIndexCellSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid-premium';

/**
 * `0` when this cell must carry the grid's tab stop because its own editor has
 * been popped out of it; `undefined` to leave MUI's computed value alone.
 *
 * Callers must SPREAD this conditionally — `GridCell` spreads `...other` after
 * its own `tabIndex`, so passing an explicit `tabIndex={undefined}` would clear
 * the roving tab stop it just computed for view mode.
 */
export const useEditModeTabStop = (rowId: GridRowId, field: string): 0 | undefined => {
  const apiRef = useGridApiContext();

  return useGridSelector(apiRef, () => {
    const rovingCell = gridTabIndexCellSelector(apiRef);

    if (rovingCell?.id !== rowId || rovingCell.field !== field) {
      return undefined;
    }

    return apiRef.current?.getCellMode(rowId, field) === 'edit' ? 0 : undefined;
  });
};
